import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '@auth0/auth0-angular';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { MessageModule } from 'primeng/message';
import { environment } from '../../../../environments/environment';
import { LoggingService } from '../../../core/services/logging.service';

interface DataExportRequest {
  id: string;
  userId: string;
  format: string;
  status: string;
  requestedAt: string;
  completedAt?: string;
  downloadUrl?: string;
}

@Component({
  selector: 'app-data-export',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    CardModule,
    MessageModule
  ],
  templateUrl: './data-export.component.html',
  styleUrl: './data-export.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataExportComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private logger = inject(LoggingService);

  isLoading = signal(false);
  isRequesting = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  pendingRequests = signal<DataExportRequest[]>([]);
  completedRequests = signal<DataExportRequest[]>([]);

  private apiUrl = environment.api_host;

  ngOnInit(): void {
    this.loadExportRequests();
  }

  private loadExportRequests(): void {
    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auth.getAccessTokenSilently().subscribe({
          next: (token) => {
            this.http.get<DataExportRequest[]>(
              `${this.apiUrl}/compliance/data-export/requests`,
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            ).subscribe({
              next: (requests) => {
                this.pendingRequests.set(
                  requests.filter(r => r.status === 'pending' || r.status === 'processing')
                );
                this.completedRequests.set(
                  requests.filter(r => r.status === 'completed')
                );
                this.isLoading.set(false);
              },
              error: (error) => {
                this.logger.error('Error loading export requests:', error);
                this.errorMessage.set('Failed to load export requests. Please try again.');
                this.isLoading.set(false);
              }
            });
          },
          error: (error) => {
            this.logger.error('Error getting token:', error);
            this.errorMessage.set('Authentication error. Please try again.');
            this.isLoading.set(false);
          }
        });
      } else {
        this.isLoading.set(false);
      }
    });
  }

  requestExport(format: 'json' | 'csv'): void {
    this.isRequesting.set(true);
    this.errorMessage.set(null);
    this.successMessage.set(null);

    this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.auth.getAccessTokenSilently().subscribe({
          next: (token) => {
            this.http.post<DataExportRequest>(
              `${this.apiUrl}/compliance/data-export/request`,
              { format },
              {
                headers: { Authorization: `Bearer ${token}` }
              }
            ).subscribe({
              next: (request) => {
                this.successMessage.set(
                  `Export request submitted successfully. You'll receive an email when your data is ready to download.`
                );
                this.isRequesting.set(false);
                // Reload requests to show the new one
                this.loadExportRequests();
              },
              error: (error) => {
                this.logger.error('Error requesting export:', error);
                this.errorMessage.set('Failed to submit export request. Please try again.');
                this.isRequesting.set(false);
              }
            });
          },
          error: (error) => {
            this.logger.error('Error getting token:', error);
            this.errorMessage.set('Authentication error. Please try again.');
            this.isRequesting.set(false);
          }
        });
      } else {
        this.isRequesting.set(false);
      }
    });
  }

  downloadExport(request: DataExportRequest): void {
    if (request.downloadUrl) {
      window.open(request.downloadUrl, '_blank');
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'processing':
        return 'Processing';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  }
}
